"use client";

import _slicedToArray from "/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useCompositeTypeahead } from "./T7VMP3TM.js";
import { SelectHeadingContext, SelectScopedContextProvider, useSelectContext } from "./KPEX55MY.js";
import { useComposite } from "./ITI7HKP4.js";
import { isHidden } from "./VGCJ63VH.js";
import { createElement, createHook, forwardRef } from "./LMDWO4NN.js";
import { useAttribute, useBooleanEvent, useEvent, useId, useMergeRefs, useTransactionState, useWrapElement } from "./ABQUS43J.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/select/select-list.tsx
import { isSelfTarget } from "@ariakit/core/utils/events";
import { invariant } from "@ariakit/core/utils/misc";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { jsx } from "react/jsx-runtime";
var TagName = "div";
var SelectListContext = createContext(null);
var useSelectList = createHook(function useSelectList2(_a) {
  var _b = _a,
    store = _b.store,
    _b$resetOnEscape = _b.resetOnEscape,
    resetOnEscape = _b$resetOnEscape === void 0 ? true : _b$resetOnEscape,
    _b$hideOnEnter = _b.hideOnEnter,
    hideOnEnter = _b$hideOnEnter === void 0 ? true : _b$hideOnEnter,
    _b$focusOnMove = _b.focusOnMove,
    focusOnMove = _b$focusOnMove === void 0 ? true : _b$focusOnMove,
    composite = _b.composite,
    alwaysVisible = _b.alwaysVisible,
    props = __objRest(_b, ["store", "resetOnEscape", "hideOnEnter", "focusOnMove", "composite", "alwaysVisible"]);
  var context = useSelectContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "SelectList must receive a `store` prop or be wrapped in a SelectProvider component.");
  var id = useId(props.id);
  var value = store.useState("value");
  var multiSelectable = Array.isArray(value);
  var _useState = useState(value),
    _useState2 = _slicedToArray(_useState, 2),
    defaultValue = _useState2[0],
    setDefaultValue = _useState2[1];
  var mounted = store.useState("mounted");
  useEffect(function () {
    if (mounted) return;
    setDefaultValue(value);
  }, [mounted, value]);
  resetOnEscape = resetOnEscape && !multiSelectable;
  var onKeyDownProp = props.onKeyDown;
  var resetOnEscapeProp = useBooleanEvent(resetOnEscape);
  var hideOnEnterProp = useBooleanEvent(hideOnEnter);
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;
    if (event.key === "Escape" && resetOnEscapeProp(event)) {
      store == null ? void 0 : store.setValue(defaultValue);
    }
    if (event.key === " " || event.key === "Enter") {
      if (isSelfTarget(event) && hideOnEnterProp(event)) {
        event.preventDefault();
        store == null ? void 0 : store.hide();
      }
    }
  });
  var headingContext = useContext(SelectHeadingContext);
  var headingState = useState();
  var _ref = headingContext || headingState,
    _ref2 = _slicedToArray(_ref, 2),
    headingId = _ref2[0],
    setHeadingId = _ref2[1];
  var headingContextValue = useMemo(function () {
    return [headingId, setHeadingId];
  }, [headingId]);
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    childStore = _useState4[0],
    setChildStore = _useState4[1];
  var setStore = useContext(SelectListContext);
  useEffect(function () {
    if (!setStore) return;
    setStore(store);
    return function () {
      return setStore(null);
    };
  }, [setStore, store]);
  props = useWrapElement(props, function (element2) {
    return /* @__PURE__ */jsx(SelectScopedContextProvider, {
      value: store,
      children: /* @__PURE__ */jsx(SelectListContext.Provider, {
        value: setChildStore,
        children: /* @__PURE__ */jsx(SelectHeadingContext.Provider, {
          value: headingContextValue,
          children: element2
        })
      })
    });
  }, [store, headingContextValue]);
  var hasCombobox = !!store.combobox;
  composite = composite != null ? composite : !hasCombobox && childStore !== store;
  var _useTransactionState = useTransactionState(composite ? store.setListElement : null),
    _useTransactionState2 = _slicedToArray(_useTransactionState, 2),
    element = _useTransactionState2[0],
    setElement = _useTransactionState2[1];
  var role = useAttribute(element, "role", props.role);
  var isCompositeRole = role === "listbox" || role === "menu" || role === "tree" || role === "grid";
  var ariaMultiSelectable = composite || isCompositeRole ? multiSelectable || void 0 : void 0;
  var hidden = isHidden(mounted, props.hidden, alwaysVisible);
  var style = hidden ? __spreadProps(__spreadValues({}, props.style), {
    display: "none"
  }) : props.style;
  if (composite) {
    props = __spreadValues({
      role: "listbox",
      "aria-multiselectable": ariaMultiSelectable
    }, props);
  }
  var labelId = store.useState(function (state) {
    var _a2;
    return headingId || ((_a2 = state.labelElement) == null ? void 0 : _a2.id);
  });
  props = __spreadProps(__spreadValues({
    id: id,
    "aria-labelledby": labelId,
    hidden: hidden
  }, props), {
    ref: useMergeRefs(setElement, props.ref),
    style: style,
    onKeyDown: onKeyDown
  });
  props = useComposite(__spreadProps(__spreadValues({
    store: store
  }, props), {
    composite: composite
  }));
  props = useCompositeTypeahead(__spreadValues({
    store: store,
    typeahead: !hasCombobox
  }, props));
  return props;
});
var SelectList = forwardRef(function SelectList2(props) {
  var htmlProps = useSelectList(props);
  return createElement(TagName, htmlProps);
});
export { useSelectList, SelectList };